import {
  Cart,
  CartConflict,
  CartConflictReason,
  CartItem,
  CartValidationResult,
  ClinicProduct,
} from '../types';

export type CartItemDtoBackend = {
  productId: string;
  /** Legacy single name; kept until backend always sends nameEn/nameZh. */
  productName?: string | null;
  nameEn?: string | null;
  nameZh?: string | null;
  productType?: 'PHYSICAL' | 'VIRTUAL' | null;
  category?: ClinicProduct['category'] | null;
  quantity: number;
  unitPrice?: number | null;
  gstRate?: number | null;
  stockStatus?: ClinicProduct['stockStatus'] | null;
  availableStock?: number | null;
  imageUrls?: string[] | null;
  imageUrl?: string | null;
  product?: {
    imageUrls?: string[] | null;
    imageUrl?: string | null;
  } | null;
};

export type CartDtoBackend = {
  items?: CartItemDtoBackend[] | null;
  subtotal: number;
  gstTotal: number;
  total: number;
};

/**
 * Backend CartItemDto is flat (productName / unitPrice / stockStatus / ...).
 * Frontend CartItem expects a nested `product: ClinicProduct`. We synthesize a
 * minimal product stub from the flat fields. Brand / spec / supplier stay
 * undefined when the backend omits them; imageUrls are read from any of the
 * shapes the backend has used (flat `imageUrls`, flat `imageUrl`, or nested
 * `product.imageUrls`).
 */
const pickImages = (item: CartItemDtoBackend): { imageUrls?: string[]; imageUrl?: string } => {
  const urls =
    item.imageUrls ?? item.product?.imageUrls ?? undefined;
  const first =
    item.imageUrl ?? item.product?.imageUrl ?? urls?.[0] ?? undefined;
  return {
    imageUrls: urls && urls.length > 0 ? urls : undefined,
    imageUrl: first || undefined,
  };
};

const safeNum = (n: number | null | undefined, fallback = 0): number =>
  typeof n === 'number' && !Number.isNaN(n) ? n : fallback;

const synthesizeProduct = (item: CartItemDtoBackend): ClinicProduct => ({
  id: item.productId,
  nameEn: item.nameEn ?? item.productName ?? '',
  nameZh: item.nameZh ?? undefined,
  productType: item.productType ?? 'PHYSICAL',
  category: item.category ?? 'OTHER',
  unit: '',
  salePrice: safeNum(item.unitPrice),
  gstRate: safeNum(item.gstRate),
  stockStatus: item.stockStatus ?? 'IN_STOCK',
  availableStock: item.availableStock ?? 0,
  ...pickImages(item),
});

export const mapCartItem = (item: CartItemDtoBackend): CartItem => ({
  productId: item.productId,
  quantity: item.quantity,
  product: synthesizeProduct(item),
  lineTotal: safeNum(item.unitPrice) * item.quantity,
});

export const mapCartDto = (dto: CartDtoBackend): Cart => ({
  items: (dto.items ?? []).map(mapCartItem),
  subtotal: dto.subtotal,
  gstTotal: dto.gstTotal,
  total: dto.total,
});

export type CartValidationResultBackend = {
  items?:
    | {
        productId: string;
        isValid: boolean;
        conflictReason?: string | null;
      }[]
    | null;
};

const KNOWN_REASONS: CartConflictReason[] = [
  'OUT_OF_STOCK',
  'INSUFFICIENT_STOCK',
  'BELOW_MOQ',
  'DELISTED',
];

const parseReason = (raw?: string | null): CartConflictReason => {
  if (raw && (KNOWN_REASONS as string[]).includes(raw)) {
    return raw as CartConflictReason;
  }
  return 'OUT_OF_STOCK';
};

export const mapCartValidation = (
  dto: CartValidationResultBackend,
  cart: Cart,
): CartValidationResult => {
  const items = dto.items ?? [];
  const conflicts: CartConflict[] = items
    .filter((i) => !i.isValid)
    .map((i) => ({
      productId: i.productId,
      reason: parseReason(i.conflictReason),
      message: i.conflictReason ?? '',
    }));
  return {
    valid: conflicts.length === 0,
    conflicts,
    cart,
  };
};
